/**
 * Het paneel 'Tekeningen op de kaart', gedeeld door de projectkaart en Vooronderzoek: per tekening
 * een aan/uit-schakelaar, de zichtbaarheid, opnieuw uitlijnen (PDF/afbeelding) en verwijderen, en
 * onderaan 'Tekening toevoegen'.
 */
import { html } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { X } from 'lucide';
import { lucideIcon } from '../icon.js';
import { layerOpacity } from './kaart-import/leaflet-layers.js';

/** Hoe een tekening op zijn plek staat, in één regel. */
function plaatsing(l) {
  if (l.kind !== 'image') return 'Op eigen coördinaten';
  const n = Array.isArray(l.georef?.controlPoints) ? l.georef.controlPoints.length : 0;
  return n ? `Uitgelijnd met ${n} paspunt${n === 1 ? '' : 'en'}` : 'Uitgelijnd';
}

/**
 * @param {object} o
 * @param {Array} o.lagen
 * @param {(l) => void} o.onToggle
 * @param {(l, opacity) => void} o.onOpacityInput   tijdens het schuiven (alleen de kaart)
 * @param {(l, opacity) => void} o.onOpacityChange  bij loslaten (bewaren)
 * @param {(l) => void} o.onRealign
 * @param {(l) => void} o.onDelete
 * @param {(l) => void} o.onZoom
 * @param {() => void} o.onAdd
 * @param {() => void} o.onClose
 */
export function renderTekeningenPaneel(o) {
  const lagen = o.lagen || [];
  return html`
    <div class="tkp" @click=${(e) => e.stopPropagation()}>
      <div class="tkp-kop">
        <strong>Tekeningen op de kaart</strong>
        <button class="tkp-x" title="Sluiten" @click=${() => o.onClose()}>${unsafeSVG(lucideIcon(X, 14))}</button>
      </div>
      ${lagen.length ? lagen.map((l) => {
        const pct = Math.round(layerOpacity(l) * 100);
        return html`
          <div class="tkp-rij">
            <span class="tkp-label ${l.kind === 'image' ? 'pdf' : 'cad'}">${l.kind === 'image' ? 'PDF' : 'CAD'}</span>
            <div class="tkp-tekst">
              <div class="tkp-naamregel">
                <div class="tkp-naam" title="Inzoomen op deze tekening" @click=${() => o.onZoom(l)}>
                  <strong class=${l.visible ? '' : 'uit'}>${l.name}</strong>
                  <span>${plaatsing(l)}</span>
                </div>
                <span role="switch" class="tkp-schakel ${l.visible ? 'aan' : ''}" aria-checked=${l.visible ? 'true' : 'false'}
                  title=${l.visible ? 'Verbergen' : 'Tonen'} @click=${() => o.onToggle(l)}><span></span></span>
              </div>
              <label class="tkp-zicht">Zichtbaarheid
                <input type="range" min="10" max="100" step="5" .value=${String(pct)}
                  @input=${(e) => { e.target.nextElementSibling.textContent = `${e.target.value}%`; o.onOpacityInput(l, Number(e.target.value) / 100); }}
                  @change=${(e) => o.onOpacityChange(l, Number(e.target.value) / 100)}>
                <span class="tkp-pct">${pct}%</span>
              </label>
              <div class="tkp-acties">
                ${l.kind === 'image' ? html`<button class="tkp-link groen" @click=${() => o.onRealign(l)}>Opnieuw uitlijnen</button>` : ''}
                <button class="tkp-link" @click=${() => o.onDelete(l)}>Verwijderen</button>
              </div>
            </div>
          </div>`;
      }) : html`<p class="tkp-leeg">Nog geen tekeningen. Voeg tekening toe om hier zichtbaarheid te bepalen.</p>`}
      <div class="tkp-voet">
        <button class="tkp-toevoegen" @click=${() => o.onAdd()}>Tekening toevoegen</button>
      </div>
    </div>`;
}

/** De stijlen; in de component via unsafeCSS(TEKENINGEN_PANEEL_CSS). Geen accenten: staat in een css-sjabloon. */
export const TEKENINGEN_PANEEL_CSS = `
  .tkp { position:absolute; right:0; top:calc(100% + 6px); width:300px; max-height:420px; overflow:auto; z-index:1100;
         background:var(--mv-surface,#fff); border:1px solid var(--mv-border); border-radius:12px; box-shadow:0 8px 24px rgba(0,0,0,.18);
         font-family:inherit; text-align:left; cursor:default; }
  .tkp-kop { display:flex; justify-content:space-between; align-items:center; padding:12px 14px 6px; }
  .tkp-kop strong { font-size:.84rem; color:var(--mv-text); }
  .tkp-x { border:none; background:none; color:var(--mv-text-muted); cursor:pointer; display:inline-flex; padding:2px; }
  .tkp-rij { display:flex; gap:10px; padding:10px 14px; border-top:1px solid var(--mv-border); }
  .tkp-rij:first-of-type { border-top:none; }
  .tkp-label { flex:none; width:28px; height:28px; border-radius:6px; display:inline-flex; align-items:center; justify-content:center;
               font-size:.5rem; font-weight:800; }
  .tkp-label.pdf { background:#fdf0e6; color:#c2410c; }
  .tkp-label.cad { background:#e8eefc; color:#3b5bdb; }
  .tkp-tekst { flex:1; min-width:0; display:flex; flex-direction:column; gap:6px; }
  .tkp-naamregel { display:flex; align-items:flex-start; gap:8px; }
  .tkp-naam { flex:1; min-width:0; display:flex; flex-direction:column; cursor:pointer; }
  .tkp-naam strong { font-size:.8rem; color:var(--mv-text); word-break:break-word; }
  .tkp-naam strong.uit { color:var(--mv-text-muted); }
  .tkp-naam span { font-size:.7rem; color:var(--mv-text-muted); }
  .tkp-schakel { flex:none; width:34px; height:20px; border-radius:999px; background:var(--mv-border); position:relative; cursor:pointer; transition:background .15s; }
  .tkp-schakel span { position:absolute; top:2px; left:2px; width:16px; height:16px; border-radius:50%; background:#fff;
                      box-shadow:0 1px 2px rgba(0,0,0,.25); transition:left .15s; }
  .tkp-schakel.aan { background:var(--mv-primary); }
  .tkp-schakel.aan span { left:16px; }
  .tkp-zicht { display:flex; align-items:center; gap:8px; font-size:.72rem; color:var(--mv-text-sub); }
  .tkp-zicht input { flex:1; min-width:0; accent-color:var(--mv-primary); }
  .tkp-pct { width:32px; text-align:right; font-variant-numeric:tabular-nums; }
  .tkp-acties { display:flex; gap:14px; }
  .tkp-link { border:none; background:none; padding:0; font:inherit; font-size:.72rem; font-weight:600; cursor:pointer; color:var(--mv-text-muted); }
  .tkp-link.groen { color:var(--mv-primary); }
  .tkp-link:hover { text-decoration:underline; }
  .tkp-leeg { margin:0; padding:4px 14px 8px; font-size:.74rem; line-height:1.45; color:var(--mv-text-sub); }
  .tkp-voet { padding:10px 14px 12px; border-top:1px solid var(--mv-border); }
  .tkp-toevoegen { width:100%; border:none; border-radius:8px; padding:8px 12px; background:var(--mv-primary); color:#fff;
                   font:inherit; font-size:.8rem; font-weight:700; cursor:pointer; }
  .tkp-toevoegen:hover { filter:brightness(1.05); }
`;
